import React, { useState, useEffect, useCallback } from 'react';
import { getRaceDrivers, setRaceDrivers, getTeamsWithExtraDrivers, getDrivers } from '../../api';

export default function AdminDriverLineup({ currentRace, setMessage }) {
    const [teamsWithExtras, setTeamsWithExtras] = useState([]);
    const [raceDriverIds, setRaceDriverIds] = useState(new Set());
    const [allDrivers, setAllDrivers] = useState([]);
    const [inherited, setInherited] = useState(false);
    const [loading, setLoading] = useState(false);

    const loadData = useCallback(async () => {
        if (!currentRace) return;
        setLoading(true);
        try {
            const [extras, rdResp, drivers] = await Promise.all([
                getTeamsWithExtraDrivers(),
                getRaceDrivers(currentRace),
                getDrivers(),
            ]);
            setTeamsWithExtras(extras || []);
            setAllDrivers(drivers || []);
            setInherited(rdResp?.inherited || false);

            // Build set of active driver IDs for this race
            const ids = new Set((rdResp?.drivers || []).map((d) => d.id));
            setRaceDriverIds(ids);
        } catch (e) {
            console.error('AdminDriverLineup load error', e);
        }
        setLoading(false);
    }, [currentRace]);

    useEffect(() => { loadData(); }, [loadData]);

    // Count how many drivers are selected for a given team
    function selectedCountForTeam(teamId) {
        const team = teamsWithExtras.find((t) => t.team_id === teamId);
        if (!team) return 0;
        return team.drivers.filter((d) => raceDriverIds.has(d.id)).length;
    }

    function toggleDriver(driverId, teamId) {
        setRaceDriverIds((prev) => {
            const next = new Set(prev);
            if (next.has(driverId)) {
                next.delete(driverId);
            } else {
                // Enforce max 2 per team
                const team = teamsWithExtras.find((t) => t.team_id === teamId);
                const currentCount = team ? team.drivers.filter((d) => next.has(d.id)).length : 0;
                if (currentCount >= 2) return prev;
                next.add(driverId);
            }
            return next;
        });
    }

    async function handleSave() {
        if (!currentRace) return setMessage('Select a race first');

        // Build full driver list: start with all drivers that belong to teams
        // with exactly 2 drivers (they're always included), plus selected drivers
        // from teams with >2 drivers.
        const extraTeamIds = new Set(teamsWithExtras.map((t) => t.team_id));
        const alwaysIncluded = allDrivers.filter((d) => !extraTeamIds.has(d.team_id)).map((d) => d.id);
        const selectedFromExtras = [...raceDriverIds].filter((id) => {
            const d = allDrivers.find((drv) => drv.id === id);
            return d && extraTeamIds.has(d.team_id);
        });

        const finalIds = [...alwaysIncluded, ...selectedFromExtras];

        try {
            await setRaceDrivers(currentRace, finalIds);
            setMessage('Driver lineup saved for this race');
            setInherited(false);
        } catch (e) {
            console.error('Error saving driver lineup', e);
            setMessage('Error saving driver lineup');
        }
    }

    if (teamsWithExtras.length === 0 && !loading) return null; // No teams need lineup management

    return (
        <>
            <h5 className="mb-3">Driver Lineup</h5>
            <div className="card mb-3">
                <div className="card-body">
                    {inherited && (
                        <div className="alert alert-info py-2 mb-3">
                            No lineup set for this race — showing inherited lineup. Save to confirm.
                        </div>
                    )}
                    {loading ? (
                        <span className="text-muted">Loading…</span>
                    ) : (
                        <>
                            <p className="text-muted small mb-3">
                                Select which drivers race for each team. Teams with only 2 drivers are automatically included.
                            </p>
                            {teamsWithExtras.map((team) => (
                                <div key={team.team_id} className="mb-3">
                                    <label className="form-label fw-semibold" style={{ color: team.team_color_primary }}>
                                        {team.team_name}
                                    </label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {team.drivers.map((d) => {
                                            const checked = raceDriverIds.has(d.id);
                                            const atLimit = !checked && selectedCountForTeam(team.team_id) >= 2;
                                            return (
                                                <div className="form-check" key={d.id}>
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`rd-${d.id}`}
                                                        checked={checked}
                                                        disabled={atLimit}
                                                        onChange={() => toggleDriver(d.id, team.team_id)}
                                                    />
                                                    <label className={`form-check-label${atLimit ? ' text-muted' : ''}`} htmlFor={`rd-${d.id}`}>
                                                        {d.code} — {d.first_name} {d.last_name}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                            <button className="btn btn-primary mt-2" type="button" onClick={handleSave}>
                                Save Driver Lineup
                            </button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
